import type { AuthTokens } from './cookieUtils'
import {
  getSecondsUntilExpiry,
  isTokenExpired,
  setTokens,
  getCSRFToken,
} from './cookieUtils'
import { refreshAccessToken } from './authApi'
import {
  BASE_BACKOFF_MS,
  MAX_REFRESH_RETRIES,
  REFRESH_THRESHOLD_SECONDS,
} from './constants'
import { acquireRefreshLock, resetRefreshLock } from './refreshLock'

const AUTH_ENDPOINTS = [
  '/auth/refresh',
  '/auth/logout',
  '/auth/login',
  '/auth/verify',
]

function isAuthEndpoint(input: RequestInfo | URL): boolean {
  const href =
    typeof input === 'string'
      ? input
      : input instanceof URL
      ? input.href
      : input.url

  return AUTH_ENDPOINTS.some((endpoint) => href.includes(endpoint))
}

type InterceptorConfig = {
  getAccessToken: () => string | null
  getRefreshToken: () => string | null
  onRefreshSuccess: (tokens: AuthTokens) => void
  onRefreshFailure: () => void
}

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function refreshWithBackoff(
  refreshToken: string,
  onSuccess: (tokens: AuthTokens) => void,
): Promise<string> {
  for (let attempt = 0; attempt < MAX_REFRESH_RETRIES; attempt++) {
    try {
      const tokens = await refreshAccessToken(refreshToken)
      setTokens(tokens)
      onSuccess(tokens)
      return tokens.access_token
    } catch (err) {
      if (attempt < MAX_REFRESH_RETRIES - 1) {
        await sleep(BASE_BACKOFF_MS * Math.pow(2, attempt))
      } else {
        throw err
      }
    }
  }
  throw new Error('Max refresh retries exceeded')
}

function isInternalRequest(input: RequestInfo | URL): boolean {
  const href =
    typeof input === 'string'
      ? input
      : input instanceof URL
      ? input.href
      : input.url
  if (!href) {
    return true
  }
  try {
    const url = new URL(href, window.location.origin)
    return url.origin === window.location.origin
  } catch {
    return true // relative URL — same origin
  }
}

function tokenNeedsRefresh(accessToken: string): boolean {
  return getSecondsUntilExpiry(accessToken) <= REFRESH_THRESHOLD_SECONDS
}

export function setupFetchInterceptor(config: InterceptorConfig): () => void {
  const originalFetch = window.fetch.bind(window)

  function makeDoRefresh(): () => Promise<string> {
    return async () => {
      const refreshToken = config.getRefreshToken()
      if (!refreshToken) {
        throw new Error('No refresh token available')
      }
      return refreshWithBackoff(refreshToken, config.onRefreshSuccess)
    }
  }

  window.fetch = async function interceptedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    if (!isInternalRequest(input)) {
      return originalFetch(input, init)
    }

    const withCredentials: RequestInit = {
      ...init,
      credentials: init?.credentials ?? 'include',
    }

    if (isAuthEndpoint(input)) {
      return originalFetch(input, withCredentials)
    }

    let accessToken = config.getAccessToken()

    if (accessToken && tokenNeedsRefresh(accessToken)) {
      try {
        accessToken = await acquireRefreshLock(makeDoRefresh())
      } catch {
        if (isTokenExpired(accessToken)) {
          config.onRefreshFailure()
        }

        return originalFetch(input, withCredentials)
      }
    }

    if (!accessToken) {
      return originalFetch(input, withCredentials)
    }

    const headers = new Headers(withCredentials.headers)
    if (!headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${accessToken}`)
    }

    const csrfToken = getCSRFToken()
    if (csrfToken) {
      headers.set('X-CSRF-Token', csrfToken)
    }

    const response = await originalFetch(input, { ...withCredentials, headers })

    if (response.status === 401 && !isAuthEndpoint(input)) {
      try {
        const newToken = await acquireRefreshLock(makeDoRefresh())
        const retryHeaders = new Headers(withCredentials.headers)
        retryHeaders.set('Authorization', `Bearer ${newToken}`)

        const freshCsrf = getCSRFToken()
        if (freshCsrf) {
          retryHeaders.set('X-CSRF-Token', freshCsrf)
        }

        return originalFetch(input, {
          ...withCredentials,
          headers: retryHeaders,
        })
      } catch {
        config.onRefreshFailure()
        return response
      }
    }

    return response
  }

  return function cleanup() {
    window.fetch = originalFetch
    resetRefreshLock()
  }
}
